import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityBodyInterface,
  ActivityTypeEnum,
  LocalizableBodyInterface,
  OfferBodyInterface,
  OfferInterface,
} from '@sport-activities/sdk';

import { Activity } from '~/core/model/activity.model';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';

@Injectable({ providedIn: 'root' })
export class ActivityDuplicationService {
  constructor(
    private readonly organizerSharedLocatableService: OrganizerSharedLocatableService,
    private readonly translateService: TranslateService
  ) {}

  private buildIndicativeOffer(
    indicativeOffer: OfferInterface
  ): OfferBodyInterface {
    const { identifier, price, currency, name, description, meta } =
      indicativeOffer;

    return {
      identifier,
      price,
      currency,
      name: this.buildLocatableBody(name),
      description: this.buildLocatableBody(description),
      meta,
    };
  }

  private buildLocatableBody<T = string | string[]>(
    value: T
  ): LocalizableBodyInterface<T>[] {
    return [
      this.organizerSharedLocatableService.buildCurrentLocatableBody(value),
    ];
  }

  public duplicate(activity: Activity): ActivityBodyInterface {
    const {
      type,
      name,
      sports,
      description,
      typicalAgeRange,
      proficiencyLevel,
      requiredEquipment,
      providedEquipment,
      indicativeMaximumAttendeeCapacity,
      indicativeMinimumAttendeeCapacity,
      validUntil,
      duration,
      location,
      image,
      organizationIdentifier,
      liveUrl,
      meta,
      refundTimeLimit,
      noteForAttendees,
    } = activity;

    let indicativeOffers;

    if (activity.indicativeOffers?.length > 0) {
      indicativeOffers = activity.indicativeOffers.map((indicativeOffer) =>
        this.buildIndicativeOffer(indicativeOffer)
      );
    }

    const nameAsCopy = this.translateService.instant(
      'activity_duplicate.title',
      { name }
    );

    return {
      type,
      name: this.buildLocatableBody(nameAsCopy),
      sports,
      description: this.buildLocatableBody(description),
      typicalAgeRange,
      proficiencyLevel,
      requiredEquipment: this.buildLocatableBody(requiredEquipment),
      providedEquipment: this.buildLocatableBody(providedEquipment),
      indicativeMaximumAttendeeCapacity,
      indicativeMinimumAttendeeCapacity,
      duration,
      validUntil,
      location:
        type !== ('LIVE' as ActivityTypeEnum)
          ? {
              mapsProvider: location.mapsProvider,
              identifier: location.identifier,
              address: location.address,
              precision: this.buildLocatableBody(location.precision),
              geo: location.geo,
            }
          : undefined,
      image: image.url,
      organizationIdentifier,
      liveUrl,
      meta,
      refundTimeLimit,
      noteForAttendees: this.buildLocatableBody(noteForAttendees),
      indicativeOffers,
    };
  }
}
